<script>
	import {
		showLoadingPopup,
		hideLoadingPopup,
	} from "./../store/loadingPopupStore.js";

	import { PRConnect } from "@projectroom/ui-lib";
	import { Section, Switch, Button } from "../../lib/ui";
	import { autoGrow } from "./autoGrow";

	export let chat;

	let documentFileRef;
	let activeTab = "text"; // "text" | "file"

	let learnStatus = null; // null | 'learning' | 'done'
	let lastLearnedText = null; // 마지막으로 학습 완료된 ragText

	// Vector RAG 폴링
	let pollTimer = null;
	let pollCount = 0;
	const POLL_MAX = 5;
	const POLL_INTERVAL_MS = 60000;

	// 텍스트가 마지막 학습 이후 변경되면 상태 초기화
	$: if (chat?.ragText !== lastLearnedText && learnStatus === "done") {
		learnStatus = null;
	}

	$: if (chat?.isUseRag() && !chat?.isDocLoaded) {
		loadDocuments();
	}

	function isVectorRag() {
		return chat?.useVectorRag === "Y";
	}

	function hasPending(docs) {
		return docs.some((d) => d.status === "pending" || d.status === "queued");
	}

	function loadDocuments() {
		if (isVectorRag()) {
			PRConnect.send(
				"/api/Rag/getVectorFiles",
				{ chatId: chat.chatId },
				(data) => {
					if (data.header.status === "success") {
						chat.setUploadedDocs(data.result?.files || []);
						if (hasPending(chat.uploadedDocs)) startPolling();
					}
				},
			);
		} else {
			PRConnect.send(
				"/api/Rag/getDocuments",
				{ chatId: chat.chatId },
				(data) => {
					if (data.header.status === "success") {
						chat.setUploadedDocs(data.result || []);
					}
				},
			);
		}
	}

	function startPolling() {
		if (pollTimer) return;
		pollCount = 0;
		scheduleNextPoll();
	}

	function scheduleNextPoll() {
		pollTimer = setTimeout(() => {
			pollCount++;
			PRConnect.send(
				"/api/Rag/checkPendingFiles",
				{ chatId: chat.chatId },
				(data) => {
					if (data.header.status === "success") {
						chat.setUploadedDocs(data.result?.files || []);
					}
					pollTimer = null;
					if (hasPending(chat.uploadedDocs) && pollCount < POLL_MAX) {
						scheduleNextPoll();
					}
				},
			);
		}, POLL_INTERVAL_MS);
	}

	function readFile(file) {
		const isPdf = file.name.toLowerCase().endsWith(".pdf");
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (event) => {
				let content = event.target.result;
				if (isPdf) {
					const bytes = new Uint8Array(content);
					let binary = "";
					for (let i = 0; i < bytes.byteLength; i++) {
						binary += String.fromCharCode(bytes[i]);
					}
					content = btoa(binary);
				}
				resolve({ content, fileType: isPdf ? "pdf" : "text" });
			};
			reader.onerror = reject;
			if (isPdf) {
				reader.readAsArrayBuffer(file);
			} else {
				reader.readAsText(file, "UTF-8");
			}
		});
	}

	function uploadFile(file, content, fileType) {
		return new Promise((resolve) => {
			PRConnect.send(
				"/api/Rag/uploadDocument",
				{
					chatId: chat.chatId,
					sourceName: file.name,
					content,
					fileType,
					replace: "N",
					ragStrict: chat.ragStrict,
				},
				(data) => resolve(data),
			);
		});
	}

	async function onDocumentFileChange(e) {
		const files = Array.from(e.target.files);
		documentFileRef.value = "";
		if (!files.length) return;

		// 파일 크기 체크 (PDF base64 변환 시 ~33% 증가 → 서버 20MB 한도 고려)
		const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
		const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
		if (oversized.length) {
			alert(
				`파일 크기가 너무 큽니다 (최대 15MB):\n${oversized.map((f) => f.name).join(", ")}`,
			);
			return;
		}

		// 중복 파일 필터링
		const duplicates = files.filter((f) =>
			chat.uploadedDocs.some((d) => d.sourceName === f.name),
		);
		if (duplicates.length) {
			alert(
				`이미 등록된 파일입니다: ${duplicates.map((f) => f.name).join(", ")}\n전체 삭제 후 다시 업로드하거나 다른 파일을 선택해주세요.`,
			);
			return;
		}

		showLoadingPopup("ragUpload");

		let anySuccess = false;
		for (const file of files) {
			try {
				const { content, fileType } = await readFile(file);
				const data = await uploadFile(file, content, fileType);
				if (data.header.status === "success") {
					anySuccess = true;
					// 업로드 응답에서 useVectorRag 즉시 반영.
					// 첫 업로드 시 chat.useVectorRag='N'인 상태에서 importVectorFiles/startPolling에
					// 진입할 수 있도록, 서버 응답 기반으로 선반영.
					if (data.result?.useVectorRag === "Y") {
						chat.setUseVectorRag("Y");
					}
					chat.addUploadedDoc({
						sourceName: file.name,
						status: isVectorRag() ? "queued" : null,
						chunkCount: data.result?.totalChunks,
					});
				}
			} catch {
				alert(`"${file.name}" 업로드 중 오류가 발생했습니다.`);
			}
		}

		if (anySuccess && isVectorRag()) {
			// queued 파일 일괄 importRagFiles (LRO 1회) 후 목록 갱신
			await new Promise((resolve) => {
				PRConnect.send(
					"/api/Rag/importVectorFiles",
					{ chatId: chat.chatId },
					() => resolve(),
				);
			});
		}

		hideLoadingPopup();

		if (anySuccess) {
			chat.setUseRag("Y");
			loadDocuments();
			if (isVectorRag()) startPolling();
		}
	}

	function structureRagText() {
		if (!chat.ragText?.trim()) return;
		if (learnStatus === "learning") return;

		// 텍스트 미변경 시 재학습 불필요
		if (chat.ragText === lastLearnedText) {
			alert(
				"텍스트가 변경되지 않았습니다. 내용을 수정한 후 다시 시도해주세요.",
			);
			return;
		}

		learnStatus = "learning";
		PRConnect.send(
			"/api/Rag/structureRagText",
			{ chatId: chat.chatId, ragStrict: chat.ragStrict },
			(data) => {
				if (data.header.status === "success") {
					learnStatus = "done";
					lastLearnedText = chat.ragText;
					chat.setUseRag("Y");
					loadDocuments();
				} else {
					learnStatus = null;
				}
			},
		);
	}

	function deleteAllDocuments() {
		if (!confirm("등록된 문서를 모두 삭제할까요?")) return;

		showLoadingPopup("ragDelete");
		PRConnect.send(
			"/api/Rag/deleteDocuments",
			{ chatId: chat.chatId },
			(data) => {
				hideLoadingPopup();
				if (data.header.status === "success") {
					learnStatus = null;
					lastLearnedText = null;
					chat.setUseRag("N");
					chat.clearUploadedDocs();
				}
			},
		);
	}
</script>

<Section title="문서 기반 답변 (RAG)" description="업로드한 문서를 참고해 AI가 답변합니다.">
	<!-- 문서에서만 답변 -->
	<div class="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-p-3 tw-font-sans">
		<div class="tw-min-w-0">
			<div class="tw-text-sm tw-font-medium tw-text-foreground">문서에서만 답변</div>
			<div class="tw-text-xs tw-text-muted-foreground">
				{chat.isRagStrict() ? "문서에 있는 내용만 답변합니다." : "문서에 없으면 AI가 보완해 답변합니다."}
			</div>
		</div>
		<Switch checked={chat.isRagStrict()} on:click={() => chat.changeRagStrict()} />
	</div>

	<!-- 탭 -->
	<div class="tw-mb-3 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-muted tw-p-1 tw-font-sans">
		<button type="button" class="tw-cursor-pointer tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors {activeTab === 'text' ? 'tw-bg-card tw-text-foreground tw-shadow-sm' : 'tw-text-muted-foreground hover:tw-text-foreground'}" on:click={() => (activeTab = "text")}>텍스트 입력</button>
		<button type="button" class="tw-cursor-pointer tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors {activeTab === 'file' ? 'tw-bg-card tw-text-foreground tw-shadow-sm' : 'tw-text-muted-foreground hover:tw-text-foreground'}" on:click={() => (activeTab = "file")}>파일 업로드</button>
	</div>

	{#if activeTab === "text"}
		<textarea class="tw-flex tw-min-h-[120px] tw-max-h-[400px] tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-text-foreground placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring" placeholder="AI가 참고할 내용을 입력하세요." bind:value={chat.ragText} use:autoGrow={chat.ragText} on:change={() => chat.save()} />
		<div class="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-font-sans">
			<Button on:click={structureRagText} disabled={!chat.ragText?.trim() || learnStatus === "learning"}>
				{learnStatus === "learning" ? "학습중..." : "학습 적용"}
			</Button>
			{#if learnStatus === "done"}<span class="tw-text-sm tw-text-primary">✓ 학습 완료</span>{/if}
		</div>
	{/if}

	{#if activeTab === "file"}
		<input type="file" accept=".txt,.md,.pdf" multiple bind:this={documentFileRef} on:change={onDocumentFileChange} hidden />
		{#if chat.uploadedDocs?.length > 0}
			<div class="tw-space-y-2 tw-font-sans">
				{#each chat.uploadedDocs as doc}
					<div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2">
						<span class="tw-truncate tw-text-sm tw-text-foreground">{doc.sourceName}</span>
						{#if doc.status === "pending" || doc.status === "queued"}
							<span class="tw-shrink-0 tw-text-xs tw-text-muted-foreground">처리중...</span>
						{:else if doc.status === "failed"}
							<span class="tw-shrink-0 tw-text-xs tw-text-destructive">실패</span>
						{:else if doc.chunkCount}
							<span class="tw-shrink-0 tw-text-xs tw-text-muted-foreground">{doc.chunkCount}개 청크</span>
						{/if}
					</div>
				{/each}
			</div>
		{:else}
			<div class="tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-p-4 tw-text-center tw-text-sm tw-text-muted-foreground tw-font-sans">등록된 문서가 없습니다. (.txt, .md, .pdf 지원)</div>
		{/if}
		<div class="tw-mt-3 tw-font-sans">
			<Button variant="outline" on:click={() => documentFileRef.click()}>파일 추가</Button>
		</div>
	{/if}

	{#if chat.uploadedDocs?.length > 0}
		<div class="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-muted-foreground tw-font-sans">
			학습된 문서 {chat.uploadedDocs.length}개
			<button type="button" class="tw-cursor-pointer tw-text-destructive hover:tw-underline" on:click={deleteAllDocuments}>전체 삭제</button>
		</div>
	{/if}
</Section>

<style>
	.rag-strict-wrap {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 12px;
		flex-wrap: wrap;
	}

	.rag-strict-label {
		font-size: 13px;
		color: hsl(var(--sb-foreground));
		font-weight: 500;
	}

	.rag-strict-desc {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
		width: 100%;
		margin-top: -4px;
	}

	.rag-tab-wrap {
		display: flex;
		gap: 0;
		margin-bottom: 10px;
		border-bottom: 1px solid hsl(var(--sb-border));
	}

	.rag-tab {
		padding: 6px 16px;
		font-size: 13px;
		background: none;
		border: none;
		cursor: pointer;
		color: hsl(var(--sb-muted-foreground));
		border-bottom: 2px solid transparent;
		margin-bottom: -1px;
	}

	.rag-tab.active {
		color: hsl(var(--sb-foreground));
		border-bottom-color: hsl(var(--sb-foreground));
		font-weight: 500;
	}

	.rag-textarea {
		width: 100%;
		min-height: 160px;
		padding: 10px;
		font-size: 13px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 6px;
		resize: vertical;
		box-sizing: border-box;
		line-height: 1.6;
		color: hsl(var(--sb-foreground));
	}

	.rag-doc-list {
		margin-top: 8px;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.rag-doc-item {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: hsl(var(--sb-muted) / 0.4);
		border-radius: 6px;
		font-size: 13px;
	}

	.rag-doc-name {
		flex: 1;
		color: hsl(var(--sb-foreground));
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.rag-doc-chunk {
		color: hsl(var(--sb-muted-foreground));
		font-size: 12px;
		flex-shrink: 0;
	}

	.rag-doc-item.pending {
		opacity: 0.7;
	}

	.rag-doc-status {
		font-size: 12px;
		flex-shrink: 0;
	}

	.rag-doc-status.pending {
		color: #d69e2e;
	}

	.rag-doc-status.failed {
		color: hsl(var(--sb-destructive));
	}

	.rag-doc-empty {
		margin-top: 8px;
		font-size: 13px;
		color: hsl(var(--sb-muted-foreground));
	}

	.rag-btn-wrap {
		margin-top: 10px;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.publishing-btn.disabled {
		opacity: 0.4;
		pointer-events: none;
	}

	.publishing-btn.delete :global(.publishing-text) {
		color: hsl(var(--sb-destructive));
	}

	.rag-learn-done {
		font-size: 12px;
		color: #38a169;
		font-weight: 500;
	}

	.rag-status {
		margin-top: 12px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.rag-delete-all {
		color: hsl(var(--sb-destructive));
		cursor: pointer;
		text-decoration: underline;
	}

	.rag-strict-toggle :global(.toggle) {
		background-color: hsl(var(--sb-input));
		transition: background-color 0.2s;
	}

	.rag-strict-toggle :global(.toggle.on) {
		background-color: hsl(var(--sb-primary));
	}
</style>
